import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTeamColors } from 'hooks';
import { Selector } from 'components';
import types from 'redux/types';
import { color } from 'theme';
import { getBackgroundName } from 'helpers';
import {
  PageContainer,
  BackgroundImage,
  BlackOverlay,
  PageHeader,
  PageTitle,
  SiteName,
  StatsGrid,
  StatCard,
  StatLabel,
  StatValue,
  ProductsTable,
  TableHeader,
  TableRow,
  TableCell,
  ErrorMessage,
  LoadingMessage,
  AccessDeniedContainer,
  SelectorContainer,
  SectionHeader,
  SectionSubHeader,
  CollapsibleSection,
  CollapsibleHeader,
  CollapsibleTitle,
  CollapsibleIcon,
  CollapsibleContent,
  SearchContainer,
  SearchInput,
  ClickableRow,
  SmallText,
  StatLabelWithHelp,
  HelpIcon,
} from './style';

/**
 * Vitals page - Admin-only analytics dashboard
 * Shows product stats, financials, and vitals for sites/apps
 * Hidden from navigation and search engines
 * @component
 */
const Vitals = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { mainColor, mainColorRgba } = useTeamColors();

  // Check if user is authenticated
  const authToken = useSelector((state) => state.user?.data?.token);
  const isUserAuth = !!authToken;

  // Get user email for display
  const userEmail = useSelector((state) => state.user?.data?.email);

  // Get configuration to determine current site
  const configuration = useSelector((state) => state.configuration?.data);
  const currentSiteName = configuration?.site_name || window.location.hostname;

  // Get product analytics data from Redux with safe defaults
  const productAnalyticsState = useSelector((state) => state.productAnalytics);
  const products = useMemo(
    () => productAnalyticsState?.data?.products || [],
    [productAnalyticsState?.data?.products]
  );
  const report = productAnalyticsState?.data?.report || null;
  const loading = productAnalyticsState?.loading || false;
  const error = productAnalyticsState?.error || null;

  // State for selected site (for downkount.com multi-site view)
  const [selectedSite, setSelectedSite] = useState(null);

  // Track if we've initialized from URL params
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);

  // State for collapsible products section
  const [isProductsOpen, setIsProductsOpen] = useState(false);

  // State for products search filter
  const [searchQuery, setSearchQuery] = useState('');

  // State for background image
  const [backgroundImage, setBackgroundImage] = useState('');

  // Track if configuration is loaded (for fade-in effect)
  const configLoading = useSelector((state) => state.configuration?.loading);
  const isConfigReady = !configLoading && configuration;

  // Load background image based on team league (matching ThemeProvider logic)
  useEffect(() => {
    if (!configuration) {
      return;
    }

    // For one-team sites (like Big Blue Time), use team-specific background
    // For Downkount (multi-league), use generic sport background
    const isOneTeamSite =
      configuration.display_one_team === '1' ||
      configuration.display_one_team === 1;

    let backgroundName;
    if (isOneTeamSite) {
      const leagueName = configuration.one_league_name?.toUpperCase();
      backgroundName = leagueName
        ? getBackgroundName(leagueName)
        : 'sport-background';
    } else {
      backgroundName = 'sport-background';
    }

    if (backgroundName) {
      import(`assets/img/${backgroundName}.jpg`)
        .then((image) => {
          setBackgroundImage(image.default);
        })
        .catch((err) => {
          setBackgroundImage(''); // Clear on error
        });
    }
  }, [configuration]);

  // Override global overflow:hidden to allow scrolling on Vitals page
  useEffect(() => {
    // Store original overflow values
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const rootElement = document.getElementById('root');

    const originalHtmlOverflow = htmlElement.style.overflow;
    const originalBodyOverflow = bodyElement.style.overflow;
    const originalRootOverflow = rootElement?.style.overflow;

    // Enable scrolling
    htmlElement.style.overflow = 'auto';
    bodyElement.style.overflow = 'auto';
    if (rootElement) {
      rootElement.style.overflow = 'auto';
    }

    // Cleanup: restore original overflow values when component unmounts
    return () => {
      htmlElement.style.overflow = originalHtmlOverflow;
      bodyElement.style.overflow = originalBodyOverflow;
      if (rootElement) {
        rootElement.style.overflow = originalRootOverflow;
      }
    };
  }, []);

  // Check if this is downkount.com (can view all sites)
  const isDownkountSite = useMemo(
    () =>
      currentSiteName?.toLowerCase().includes('downkount') ||
      window.location.hostname.toLowerCase().includes('downkount'),
    [currentSiteName]
  );

  // Load products overview on mount
  useEffect(() => {
    if (!authToken) {
      return;
    }
    dispatch({
      type: types.PRODUCT_ANALYTICS_OVERVIEW_START,
      payload: { token: authToken },
    });
  }, [dispatch, authToken]);

  // Load report for selected site
  useEffect(() => {
    if (selectedSite && authToken) {
      dispatch({
        type: types.PRODUCT_ANALYTICS_REPORT_START,
        payload: {
          token: authToken,
          siteUrl: selectedSite.value, // Use site_url (stable identifier)
          startDate: null, // Can add date range selector later
          endDate: null,
        },
      });
    }
  }, [dispatch, selectedSite, authToken]);

  // Initialize from URL parameter on mount
  useEffect(() => {
    if (initializedFromUrl || !products || products.length === 0) return;

    const params = new URLSearchParams(location.search);
    const productParam = params.get('product');

    if (productParam) {
      // Find product by app_file_name (e.g., 'dubtime')
      const product = products.find(
        (p) => p.app_file_name?.toLowerCase() === productParam.toLowerCase()
      );

      if (product) {
        setSelectedSite({
          value: product.site_url || product.site_name || 'Unknown',
          label: product.site_name || product.site_url || 'Unknown',
        });
        setInitializedFromUrl(true);
        return;
      }
    }

    // No URL param or product not found - use default behavior
    if (!isDownkountSite && currentSiteName && !selectedSite) {
      setSelectedSite({
        value: currentSiteName,
        label: currentSiteName,
      });
    } else if (isDownkountSite && !selectedSite && currentSiteName) {
      // For downkount, default to downkount itself
      setSelectedSite({
        value: currentSiteName,
        label: currentSiteName,
      });
    }

    setInitializedFromUrl(true);
  }, [
    isDownkountSite,
    currentSiteName,
    selectedSite,
    products,
    location.search,
    initializedFromUrl,
  ]);

  // Create options for site selector (only for downkount.com)
  // Use site_url as value (stable identifier) and site_name for display
  // Sort alphabetically by app name (label)
  const siteOptions = useMemo(() => {
    if (!products || !Array.isArray(products) || products.length === 0) {
      return [];
    }

    const options = products
      .map((product) => ({
        value: product.site_url || product.site_name || 'Unknown',
        label: product.site_name || product.site_url || 'Unknown',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    console.log('[Vitals] Site options created:', options.slice(0, 3)); // Log first 3
    return options;
  }, [products]);

  // Filter products based on search query
  const filteredProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    if (!searchQuery.trim()) return products;

    const query = searchQuery.toLowerCase();
    return products.filter((product) => {
      const siteName = (product?.site_name || '').toLowerCase();
      const teamName = (product?.team_name || '').toLowerCase();
      const appName = (product?.app_file_name || '').toLowerCase();
      return (
        siteName.includes(query) ||
        teamName.includes(query) ||
        appName.includes(query)
      );
    });
  }, [products, searchQuery]);

  // Handle site selection change
  const handleSiteChange = (selected) => {
    setSelectedSite(selected);

    // Update URL parameter
    if (selected && products && products.length > 0) {
      const product = products.find(
        (p) => p.site_url === selected.value || p.site_name === selected.value
      );

      if (product && product.app_file_name) {
        const params = new URLSearchParams(location.search);
        params.set('product', product.app_file_name);
        navigate(`?${params.toString()}`, { replace: true });
      }
    }
  };

  // Handle product row click
  const handleProductClick = (product) => {
    const siteToSelect = {
      value: product.site_url || product.site_name || 'Unknown',
      label: product.site_name || product.site_url || 'Unknown',
    };
    setSelectedSite(siteToSelect);

    // Update URL parameter
    if (product && product.app_file_name) {
      const params = new URLSearchParams(location.search);
      params.set('product', product.app_file_name);
      navigate(`?${params.toString()}`, { replace: true });
    }
    setIsProductsOpen(false);
    // Scroll to top to see the report
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Find current site data (must be before early return)
  // Match by site_url (stable identifier)
  const currentSiteData = useMemo(() => {
    if (!products || !Array.isArray(products) || !selectedSite) return null;
    return products.find(
      (p) =>
        p?.site_url === selectedSite?.value ||
        p?.site_name === selectedSite?.value
    );
  }, [products, selectedSite]);

  // Authentication required - only logged in users can access
  if (!isUserAuth) {
    return (
      <>
        <Helmet>
          <title>Access Denied - Vitals</title>
          <meta content="noindex, nofollow" name="robots" />
        </Helmet>
        <AccessDeniedContainer>
          <h2>Access Denied</h2>
          <p>This page is only available to logged in users.</p>
          <p style={{ fontSize: '1.4rem', marginTop: '1rem', color: '#999' }}>
            Please log in to view this page.
          </p>
        </AccessDeniedContainer>
      </>
    );
  }

  // If API returns 403 or admin-related error, show access denied
  if (
    error &&
    (error.includes('403') ||
      error.includes('admin') ||
      error.includes('Unauthorized') ||
      error.includes('Forbidden'))
  ) {
    return (
      <>
        <Helmet>
          <title>Access Denied - Vitals</title>
          <meta content="noindex, nofollow" name="robots" />
        </Helmet>
        <AccessDeniedContainer>
          <h2>Access Denied</h2>
          <p>This page is only available to administrators.</p>
          <p style={{ fontSize: '1.4rem', marginTop: '1rem', color: '#999' }}>
            {error}
          </p>
        </AccessDeniedContainer>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Vitals - {currentSiteName}</title>
        <meta content="noindex, nofollow" name="robots" />
      </Helmet>

      {/* Background image (fixed) */}
      {backgroundImage ? <BackgroundImage $imgUrl={backgroundImage} /> : null}

      {/* Black overlay at 50% opacity */}
      <BlackOverlay />

      <PageContainer $isReady={isConfigReady}>
        <PageHeader $mainColor={mainColor}>
          <PageTitle>Vitals</PageTitle>
          {userEmail ? (
            <div style={{ fontSize: '1.4rem', color: color.greyText }}>
              {userEmail}
            </div>
          ) : null}
        </PageHeader>

        {/* Site Selector - only show for downkount.com */}
        {isDownkountSite && Boolean(siteOptions.length) ? (
          <SelectorContainer>
            <Selector
              onChange={handleSiteChange}
              options={siteOptions}
              placeholder="Select Site"
              title=""
              value={selectedSite}
            />
          </SelectorContainer>
        ) : null}

        {loading ? (
          <LoadingMessage>Loading analytics data...</LoadingMessage>
        ) : null}

        {error ? <ErrorMessage>Error: {error}</ErrorMessage> : null}

        {/* Current Site Stats */}
        {currentSiteData ? (
          <>
            <SiteName $mainColor={mainColor}>
              <span className="site-title">
                {String(currentSiteData.site_name || 'Unknown Site')}
              </span>
              {currentSiteData.site_url ? (
                <span className="site-url">({currentSiteData.site_url})</span>
              ) : null}
            </SiteName>

            <StatsGrid>
              <StatCard $mainColor={mainColor} $mainColorRgba={mainColorRgba}>
                <StatLabel>Team</StatLabel>
                <StatValue>
                  {String(currentSiteData.team_name || 'N/A')}
                </StatValue>
              </StatCard>

              <StatCard $mainColor={mainColor} $mainColorRgba={mainColorRgba}>
                <StatLabel>App Name</StatLabel>
                <StatValue>
                  {String(currentSiteData.app_file_name || 'N/A')}
                </StatValue>
              </StatCard>

              <StatCard $mainColor={mainColor} $mainColorRgba={mainColorRgba}>
                <StatLabel>iOS App</StatLabel>
                <StatValue>
                  {currentSiteData.has_ios_app ? '✅ Yes' : '❌ No'}
                </StatValue>
              </StatCard>

              <StatCard $mainColor={mainColor} $mainColorRgba={mainColorRgba}>
                <StatLabel>Android App</StatLabel>
                <StatValue>
                  {currentSiteData.has_android_app ? '✅ Yes' : '❌ No'}
                </StatValue>
              </StatCard>
            </StatsGrid>
          </>
        ) : null}

        {/* Report Data */}
        {report ? (
          <>
            <SectionHeader $mainColor={mainColor} $marginTop="3rem">
              Analytics Report
            </SectionHeader>
            <StatsGrid>
              {/* Use profitability object for aggregated totals */}
              {report?.profitability ? (
                <>
                  <StatCard
                    $mainColor={mainColor}
                    $mainColorRgba={mainColorRgba}
                  >
                    <StatLabelWithHelp>
                      <StatLabel>Total Revenue (30 days)</StatLabel>
                      <HelpIcon
                        $mainColor={mainColor}
                        $mainColorRgba={mainColorRgba}
                        data-tooltip="All income from apps and ads"
                      >
                        ?
                      </HelpIcon>
                    </StatLabelWithHelp>
                    <StatValue>
                      $
                      {parseFloat(
                        report.profitability.total_revenue || 0
                      ).toFixed(2)}
                    </StatValue>
                  </StatCard>

                  <StatCard
                    $mainColor={mainColor}
                    $mainColorRgba={mainColorRgba}
                  >
                    <StatLabelWithHelp>
                      <StatLabel>Total Expenses (30 days)</StatLabel>
                      <HelpIcon
                        $mainColor={mainColor}
                        $mainColorRgba={mainColorRgba}
                        data-tooltip="Domain, hosting, and infrastructure costs"
                      >
                        ?
                      </HelpIcon>
                    </StatLabelWithHelp>
                    <StatValue>
                      $
                      {parseFloat(
                        report.profitability.total_expenses || 0
                      ).toFixed(2)}
                    </StatValue>
                  </StatCard>

                  <StatCard
                    $mainColor={mainColor}
                    $mainColorRgba={mainColorRgba}
                  >
                    <StatLabelWithHelp>
                      <StatLabel>Net Profit (30 days)</StatLabel>
                      <HelpIcon
                        $mainColor={mainColor}
                        $mainColorRgba={mainColorRgba}
                        data-tooltip="Total revenue minus total expenses"
                      >
                        ?
                      </HelpIcon>
                    </StatLabelWithHelp>
                    <StatValue
                      style={{
                        color:
                          parseFloat(report.profitability.net_profit || 0) > 0
                            ? '#10b981'
                            : parseFloat(report.profitability.net_profit || 0) <
                                0
                              ? '#ef4444'
                              : undefined,
                      }}
                    >
                      $
                      {parseFloat(report.profitability.net_profit || 0).toFixed(
                        2
                      )}
                    </StatValue>
                  </StatCard>

                  {report.profitability.profit_margin_percent !== undefined ? (
                    <StatCard
                      $mainColor={mainColor}
                      $mainColorRgba={mainColorRgba}
                    >
                      <StatLabelWithHelp>
                        <StatLabel>Profit Margin</StatLabel>
                        <HelpIcon
                          $mainColor={mainColor}
                          $mainColorRgba={mainColorRgba}
                          data-tooltip="Net profit as a percentage of revenue"
                        >
                          ?
                        </HelpIcon>
                      </StatLabelWithHelp>
                      <StatValue>
                        {parseFloat(
                          report.profitability.profit_margin_percent || 0
                        ).toFixed(1)}
                        %
                      </StatValue>
                    </StatCard>
                  ) : null}
                </>
              ) : null}

              {report?.revenue?.total?.[0]?.total_transactions &&
              report.revenue.total[0].total_transactions > 0 ? (
                <StatCard $mainColor={mainColor} $mainColorRgba={mainColorRgba}>
                  <StatLabelWithHelp>
                    <StatLabel>Total Transactions</StatLabel>
                    <HelpIcon
                      $mainColor={mainColor}
                      $mainColorRgba={mainColorRgba}
                      data-tooltip="Number of in-app purchases (App Store/Google Play)"
                    >
                      ?
                    </HelpIcon>
                  </StatLabelWithHelp>
                  <StatValue>
                    {report.revenue.total[0].total_transactions}
                  </StatValue>
                </StatCard>
              ) : null}

              {report?.statistics?.by_source?.find(
                (s) => s.stat_type === 'active_users'
              ) ? (
                <StatCard $mainColor={mainColor} $mainColorRgba={mainColorRgba}>
                  <StatLabelWithHelp>
                    <StatLabel>Active Users (Avg)</StatLabel>
                    <HelpIcon
                      $mainColor={mainColor}
                      $mainColorRgba={mainColorRgba}
                      data-tooltip="Average daily active users"
                    >
                      ?
                    </HelpIcon>
                  </StatLabelWithHelp>
                  <StatValue>
                    {Number(
                      report.statistics.by_source.find(
                        (s) => s.stat_type === 'active_users'
                      ).avg_value
                    ).toLocaleString()}
                  </StatValue>
                </StatCard>
              ) : null}
            </StatsGrid>

            {/* Revenue Breakdown - Combine AdMob entries */}
            {report?.revenue?.by_source?.length > 0 ? (
              <>
                <SectionSubHeader $mainColor={mainColor} $marginTop="2.5rem">
                  Revenue Breakdown
                </SectionSubHeader>
                <StatsGrid>
                  {/* Group by revenue_source and sum totals */}
                  {Object.entries(
                    report.revenue.by_source.reduce((acc, source) => {
                      const key = source.revenue_source;
                      if (!acc[key]) {
                        acc[key] = {
                          revenue_source: key,
                          total_revenue: 0,
                        };
                      }
                      acc[key].total_revenue += parseFloat(
                        source.total_revenue || 0
                      );
                      return acc;
                    }, {})
                  ).map(([key, source]) => (
                    <StatCard
                      $mainColor={mainColor}
                      $mainColorRgba={mainColorRgba}
                      key={key}
                    >
                      <StatLabelWithHelp>
                        <StatLabel>
                          {source.revenue_source === 'app_store'
                            ? 'App Store'
                            : source.revenue_source === 'google_play'
                              ? 'Google Play'
                              : source.revenue_source === 'admob'
                                ? 'AdMob'
                                : source.revenue_source}
                        </StatLabel>
                        <HelpIcon
                          $mainColor={mainColor}
                          $mainColorRgba={mainColorRgba}
                          data-tooltip={
                            source.revenue_source === 'app_store'
                              ? 'Revenue from iOS App Store purchases'
                              : source.revenue_source === 'google_play'
                                ? 'Revenue from Google Play purchases'
                                : source.revenue_source === 'admob'
                                  ? 'Total ad revenue from all platforms'
                                  : 'Revenue from this source'
                          }
                        >
                          ?
                        </HelpIcon>
                      </StatLabelWithHelp>
                      <StatValue>${source.total_revenue.toFixed(2)}</StatValue>
                    </StatCard>
                  ))}
                </StatsGrid>
              </>
            ) : null}

            {/* App Vitals - Google Play - Show crash_rate and anr_rate */}
            {report?.statistics?.aggregated?.filter(
              (s) => s.stat_type === 'crash_rate' || s.stat_type === 'anr_rate'
            ).length > 0 ? (
              <>
                <SectionSubHeader $mainColor={mainColor} $marginTop="2.5rem">
                  Google Play Stability
                </SectionSubHeader>
                <StatsGrid>
                  {report.statistics.aggregated
                    .filter(
                      (s) =>
                        s.stat_type === 'crash_rate' ||
                        s.stat_type === 'anr_rate'
                    )
                    .map((stat) => (
                      <StatCard
                        $mainColor={mainColor}
                        $mainColorRgba={mainColorRgba}
                        key={`google-play-${stat.stat_type}`}
                      >
                        {stat.stat_type === 'crash_rate' ||
                        stat.stat_type === 'anr_rate' ? (
                          <StatLabelWithHelp>
                            <StatLabel>
                              {stat.stat_type === 'crash_rate'
                                ? 'Crash Rate'
                                : 'ANR Rate'}
                            </StatLabel>
                            <HelpIcon
                              $mainColor={mainColor}
                              $mainColorRgba={mainColorRgba}
                              data-tooltip={
                                stat.stat_type === 'crash_rate'
                                  ? 'Percentage of sessions that crash'
                                  : 'App Not Responding - app freezes/hangs'
                              }
                            >
                              ?
                            </HelpIcon>
                          </StatLabelWithHelp>
                        ) : (
                          <StatLabel>
                            {stat.stat_type &&
                            typeof stat.stat_type === 'string'
                              ? stat.stat_type.replace('_', ' ').toUpperCase()
                              : 'Unknown'}
                          </StatLabel>
                        )}
                        <StatValue>
                          {parseFloat(stat.latest_value).toFixed(2)}%
                        </StatValue>
                        <SmallText>
                          Avg: {parseFloat(stat.avg_value).toFixed(2)}%
                        </SmallText>
                      </StatCard>
                    ))}
                </StatsGrid>
              </>
            ) : null}

            {/* AdMob Performance - Only show if has meaningful data */}
            {report?.revenue?.by_source?.filter(
              (r) =>
                r.revenue_source === 'admob' && parseFloat(r.total_revenue) > 0
            ).length > 0 ? (
              <>
                <SectionSubHeader $mainColor={mainColor} $marginTop="2.5rem">
                  AdMob Performance
                </SectionSubHeader>
                <StatsGrid>
                  {report.revenue.by_source
                    .filter((r) => r.revenue_source === 'admob')
                    .map((admob) => (
                      <StatCard
                        $mainColor={mainColor}
                        $mainColorRgba={mainColorRgba}
                        key={`admob-${admob.revenue_platform}`}
                      >
                        <StatLabelWithHelp>
                          <StatLabel>
                            AdMob (
                            {admob.revenue_platform === 'ios'
                              ? 'iOS'
                              : 'Android'}
                            )
                          </StatLabel>
                          <HelpIcon
                            $mainColor={mainColor}
                            $mainColorRgba={mainColorRgba}
                            data-tooltip="Ad revenue with impressions, clicks, eCPM (earnings per 1000 impressions), and CTR (click-through rate)"
                          >
                            ?
                          </HelpIcon>
                        </StatLabelWithHelp>
                        <StatValue>${admob.total_revenue}</StatValue>
                        {admob.total_impressions || admob.avg_ecpm ? (
                          <SmallText>
                            {admob.total_impressions
                              ? `${parseInt(admob.total_impressions).toLocaleString()} impressions`
                              : ''}
                            {admob.total_clicks
                              ? ` • ${parseInt(admob.total_clicks).toLocaleString()} clicks`
                              : ''}
                            {admob.avg_ecpm
                              ? ` • eCPM: $${parseFloat(admob.avg_ecpm).toFixed(2)}`
                              : ''}
                            {admob.ctr
                              ? ` • CTR: ${parseFloat(admob.ctr).toFixed(2)}%`
                              : ''}
                          </SmallText>
                        ) : null}
                      </StatCard>
                    ))}
                </StatsGrid>
              </>
            ) : null}

            {/* Expenses Breakdown - Only show expenses with actual amounts */}
            {report?.expenses?.by_category?.filter(
              (e) => parseFloat(e.total_amount_monthly || 0) > 0
            ).length > 0 ? (
              <>
                <SectionSubHeader $mainColor={mainColor} $marginTop="2.5rem">
                  Expenses Breakdown (Monthly)
                </SectionSubHeader>
                <StatsGrid>
                  {report.expenses.by_category
                    .filter((e) => parseFloat(e.total_amount_monthly || 0) > 0)
                    .map((expense) => (
                      <StatCard
                        $mainColor={mainColor}
                        $mainColorRgba={mainColorRgba}
                        key={`${expense.expense_category}-${expense.vendor}`}
                      >
                        <StatLabelWithHelp>
                          <StatLabel>
                            {expense.expense_category === 'infrastructure'
                              ? 'Infrastructure'
                              : expense.expense_category === 'domain'
                                ? 'Domain'
                                : expense.expense_category}
                          </StatLabel>
                          <HelpIcon
                            $mainColor={mainColor}
                            $mainColorRgba={mainColorRgba}
                            data-tooltip={
                              expense.expense_category === 'infrastructure'
                                ? 'Hosting and server costs'
                                : expense.expense_category === 'domain'
                                  ? 'Domain registration and renewal'
                                  : 'Monthly cost for this service'
                            }
                          >
                            ?
                          </HelpIcon>
                        </StatLabelWithHelp>
                        <StatValue>
                          $
                          {parseFloat(
                            expense.total_amount_monthly || 0
                          ).toFixed(2)}
                        </StatValue>
                        {expense.vendor ? (
                          <SmallText>
                            {expense.vendor}
                            {expense.total_amount_yearly
                              ? ` • $${parseFloat(expense.total_amount_yearly).toFixed(2)}/year`
                              : ''}
                          </SmallText>
                        ) : null}
                      </StatCard>
                    ))}
                </StatsGrid>
              </>
            ) : null}
          </>
        ) : null}

        {/* App Store Connect Performance - iOS metrics */}
        {report?.statistics?.aggregated?.filter(
          (s) =>
            ['downloads', 'impressions', 'installs', 'deletions'].includes(
              s.stat_type
            ) && parseFloat(s.latest_value || 0) > 0
        ).length > 0 ? (
          <>
            <SectionSubHeader $mainColor={mainColor} $marginTop="2.5rem">
              App Store Connect (iOS)
            </SectionSubHeader>
            <StatsGrid>
              {report.statistics.aggregated
                .filter(
                  (s) =>
                    [
                      'downloads',
                      'impressions',
                      'installs',
                      'deletions',
                    ].includes(s.stat_type) &&
                    parseFloat(s.latest_value || 0) > 0
                )
                .map((stat) => (
                  <StatCard
                    $mainColor={mainColor}
                    $mainColorRgba={mainColorRgba}
                    key={`app-store-${stat.stat_type}`}
                  >
                    <StatLabelWithHelp>
                      <StatLabel>
                        {stat.stat_type === 'downloads'
                          ? 'Downloads'
                          : stat.stat_type === 'impressions'
                            ? 'Impressions'
                            : stat.stat_type === 'installs'
                              ? 'Installs'
                              : stat.stat_type === 'deletions'
                                ? 'Deletions'
                                : stat.stat_type
                                  ? stat.stat_type
                                      .replace('_', ' ')
                                      .toUpperCase()
                                  : 'Unknown'}
                      </StatLabel>
                      <HelpIcon
                        $mainColor={mainColor}
                        $mainColorRgba={mainColorRgba}
                        data-tooltip={
                          stat.stat_type === 'downloads'
                            ? 'Number of times app was downloaded'
                            : stat.stat_type === 'impressions'
                              ? 'App Store page views'
                              : stat.stat_type === 'installs'
                                ? 'Successful app installations'
                                : stat.stat_type === 'deletions'
                                  ? 'Number of app uninstalls'
                                  : 'App Store metric'
                        }
                      >
                        ?
                      </HelpIcon>
                    </StatLabelWithHelp>
                    <StatValue>
                      {Number(stat.latest_value).toLocaleString()}
                    </StatValue>
                    <SmallText>
                      Avg: {Number(stat.avg_value).toLocaleString()}
                    </SmallText>
                  </StatCard>
                ))}
            </StatsGrid>
          </>
        ) : null}

        {/* All Products Table - only for downkount.com - Collapsible with search */}
        {isDownkountSite && Array.isArray(products) && products.length > 0 ? (
          <CollapsibleSection $mainColor={mainColor}>
            <CollapsibleHeader
              $mainColor={mainColor}
              $mainColorRgba={mainColorRgba}
              onClick={() => setIsProductsOpen(!isProductsOpen)}
            >
              <CollapsibleTitle>
                All Products ({products.length})
              </CollapsibleTitle>
              <CollapsibleIcon $isOpen={isProductsOpen}>▼</CollapsibleIcon>
            </CollapsibleHeader>

            <CollapsibleContent $isOpen={isProductsOpen}>
              <SearchContainer>
                <SearchInput
                  $mainColor={mainColor}
                  $mainColorRgba={mainColorRgba}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by site name, team, or app..."
                  type="text"
                  value={searchQuery}
                />
              </SearchContainer>

              <ProductsTable>
                <thead>
                  <TableHeader $mainColor={mainColor}>
                    <TableCell as="th">Site Name</TableCell>
                    <TableCell as="th">Team</TableCell>
                    <TableCell as="th">App</TableCell>
                    <TableCell as="th">iOS</TableCell>
                    <TableCell as="th">Android</TableCell>
                  </TableHeader>
                </thead>
                <tbody>
                  {filteredProducts.length > 0 ? (
                    filteredProducts.map((product, index) => (
                      <ClickableRow
                        $mainColorRgba={mainColorRgba}
                        key={product?.site_id || `product-${index}`}
                        onClick={() => handleProductClick(product)}
                      >
                        <TableCell>{product?.site_name || '-'}</TableCell>
                        <TableCell>{product?.team_name || '-'}</TableCell>
                        <TableCell>{product?.app_file_name || '-'}</TableCell>
                        <TableCell>
                          {product?.has_ios_app ? '✅' : '❌'}
                        </TableCell>
                        <TableCell>
                          {product?.has_android_app ? '✅' : '❌'}
                        </TableCell>
                      </ClickableRow>
                    ))
                  ) : (
                    <TableRow $mainColorRgba={mainColorRgba}>
                      <TableCell
                        colSpan="5"
                        style={{ textAlign: 'center', padding: '2rem' }}
                      >
                        No products found matching "{searchQuery}"
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </ProductsTable>
            </CollapsibleContent>
          </CollapsibleSection>
        ) : null}
      </PageContainer>
    </>
  );
};

export default Vitals;
